import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiDownload, FiCopy, FiHelpCircle, FiUser, FiCpu, FiTrendingUp } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { generateInterviewQuestions } from '../../services/interviewService';
import { exportInterviewQuestions } from '../../services/exportService';
import toast from 'react-hot-toast';

const InterviewQuestionsModal = ({ isOpen, onClose, jobData, formData }) => {
  const [questions, setQuestions] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(['general', 'technical', 'behavioral']);
  const [questionCount, setQuestionCount] = useState(15);

  const categories = [
    { id: 'general', name: 'General', icon: FiUser, description: 'Basic role and experience questions' },
    { id: 'technical', name: 'Technical', icon: FiCpu, description: 'Role-specific technical skills' },
    { id: 'behavioral', name: 'Behavioral', icon: FiTrendingUp, description: 'Situational and soft skills' },
    { id: 'leadership', name: 'Leadership', icon: FiUser, description: 'Management and leadership scenarios' },
    { id: 'problem-solving', name: 'Problem Solving', icon: FiCpu, description: 'Critical thinking and analysis' }
  ];

  const handleGenerate = async () => {
    if (selectedCategories.length === 0) {
      toast.error('Please select at least one question category');
      return;
    }

    setIsGenerating(true);
    try {
      const result = await generateInterviewQuestions({
        jobData,
        formData,
        categories: selectedCategories,
        count: questionCount
      });
      setQuestions(result);
      toast.success('Interview questions generated successfully!');
    } catch (error) {
      console.error('Error generating questions:', error);
      toast.error('Failed to generate interview questions. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExport = async (format) => {
    if (!questions) return;

    try {
      const result = await exportInterviewQuestions(questions, format, formData);
      
      if (format === 'clipboard') {
        await navigator.clipboard.writeText(result.content);
        toast.success('Questions copied to clipboard!');
      } else {
        const blob = new Blob([result.content], { type: result.mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Questions exported as ${format.toUpperCase()}!`);
      }
    } catch (error) {
      toast.error(`Failed to export questions as ${format}`);
    }
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-purple-700">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiHelpCircle} className="h-6 w-6 text-white" />
                <h3 className="text-xl font-semibold text-white">Generate Interview Questions</h3>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiX} className="h-5 w-5 text-white" />
              </motion.button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {!questions ? (
                <div className="space-y-6">
                  {/* Question Categories */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Select Question Categories</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {categories.map((category) => (
                        <motion.button
                          key={category.id}
                          type="button"
                          onClick={() => toggleCategory(category.id)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                            selectedCategories.includes(category.id)
                              ? 'border-purple-500 bg-purple-50 shadow-md'
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                              selectedCategories.includes(category.id) ? 'bg-purple-500' : 'bg-gray-400'
                            }`}>
                              <SafeIcon icon={category.icon} className="h-4 w-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h5 className={`text-sm font-medium ${
                                selectedCategories.includes(category.id) ? 'text-purple-900' : 'text-gray-900'
                              }`}>
                                {category.name}
                              </h5>
                              <p className={`text-xs mt-1 ${
                                selectedCategories.includes(category.id) ? 'text-purple-700' : 'text-gray-500'
                              }`}>
                                {category.description}
                              </p>
                            </div>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Question Count */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Questions: {questionCount}
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="30"
                      value={questionCount}
                      onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>5 questions</span>
                      <span>30 questions</span>
                    </div>
                  </div>

                  {/* Generate Button */}
                  <motion.button
                    whileHover={{ scale: isGenerating ? 1 : 1.02 }}
                    whileTap={{ scale: isGenerating ? 1 : 0.98 }}
                    onClick={handleGenerate}
                    disabled={isGenerating || selectedCategories.length === 0}
                    className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating Questions...
                      </>
                    ) : (
                      <>
                        <SafeIcon icon={FiHelpCircle} className="mr-2 h-4 w-4" />
                        Generate Interview Questions
                      </>
                    )}
                  </motion.button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Questions Display */}
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 shadow-sm">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Generated Interview Questions</h4>
                    
                    {Object.entries(questions.categorizedQuestions).map(([category, categoryQuestions]) => (
                      <div key={category} className="mb-6">
                        <h5 className="text-md font-medium text-purple-700 mb-3 capitalize border-b border-purple-200 pb-1">
                          {category.replace('-', ' ')} Questions
                        </h5>
                        <ol className="space-y-3">
                          {categoryQuestions.map((question, index) => (
                            <li key={index} className="flex items-start space-x-3">
                              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-medium">
                                {index + 1}
                              </span>
                              <p className="text-gray-700 leading-relaxed">{question}</p>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>

                  {/* Export Options */}
                  <div className="flex flex-wrap gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleExport('clipboard')}
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                    >
                      <SafeIcon icon={FiCopy} className="h-4 w-4" />
                      <span>Copy to Clipboard</span>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleExport('pdf')}
                      className="flex items-center space-x-2 px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                    >
                      <SafeIcon icon={FiDownload} className="h-4 w-4" />
                      <span>Export as PDF</span>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleExport('docx')}
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                    >
                      <SafeIcon icon={FiDownload} className="h-4 w-4" />
                      <span>Export as Word</span>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setQuestions(null)}
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                    >
                      <SafeIcon icon={FiHelpCircle} className="h-4 w-4" />
                      <span>Generate New Set</span>
                    </motion.button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InterviewQuestionsModal;